import JSZip from 'jszip';
import { EVAL_TYPES } from '@/pages/eval/eval-builder/constants/evalTypes';

const normalizeCriterionGraderType = (criterion = {}) => {
    if (criterion.graderType === 'ExactMatch' || criterion.grader_type === 'ExactMatch') {
        return 'ExactMatch';
    }
    if (criterion.graderType === 'llm-judge' || criterion.grader_type === 'llm-judge') {
        return 'llm-judge';
    }
    if (criterion.type === 'exact' || criterion.type === 'exact_match') {
        return 'ExactMatch';
    }
    return 'llm-judge';
};

const serializeCriteria = (criteria = [], isAgentic = false) => (
    criteria.map((criterion) => {
        const { graderType, grader_type, type, ...rest } = criterion || {};
        if (isAgentic) {
            return {
                ...rest,
                grader_type: normalizeCriterionGraderType({ graderType, grader_type, type })
            };
        }
        return {
            ...rest,
            type: 'semantic',
            grader_type: 'llm-judge'
        };
    })
);

const getTasksArrayError = (tasks) => {
    if (!tasks) return 'Tasks array is required';
    if (!Array.isArray(tasks)) return 'Tasks must be an array';
    if (tasks.length === 0) return 'Please add at least one task';
    return null;
};

const getTaskFieldErrors = (task, isAgentic) => {
    const err = { prompt: false, answer: false, criteria: false };
    if (!task.prompt || typeof task.prompt !== 'string' || task.prompt.trim() === '') err.prompt = true;
    if (!task.answer || typeof task.answer !== 'string' || task.answer.trim() === '') err.answer = true;
    if (isAgentic && (!Array.isArray(task.criteria) || task.criteria.length === 0)) err.criteria = true;
    return err;
};

const collectTaskValidation = (tasks, isAgentic) => {
    const errors = [];
    const fieldErrors = {};
    for (const [index, task] of tasks.entries()) {
        const taskNumber = index + 1;
        if (!task || typeof task !== 'object') {
            errors.push(`Task ${taskNumber} is not a valid object`);
            continue;
        }
        const fieldErrorKey = task.id ?? `task-${index}`;
        fieldErrors[fieldErrorKey] = getTaskFieldErrors(task, isAgentic);
    }
    return { errors, fieldErrors };
};

const hasAnyFieldError = (fieldErrors) => (
    Object.values(fieldErrors).some(e => e.prompt || e.answer || e.criteria)
);

export const validateEvalBuilderTasks = (tasks, evalType = EVAL_TYPES.QA) => {
    const errors = [];
    const fieldErrors = {};
    const isAgentic = evalType === EVAL_TYPES.AGENTIC;

    const arrayError = getTasksArrayError(tasks);
    if (arrayError) {
        errors.push(arrayError);
        return { isValid: false, errors, fieldErrors };
    }

    const { errors: taskErrors, fieldErrors: taskFieldErrors } = collectTaskValidation(tasks, isAgentic);
    errors.push(...taskErrors);
    Object.assign(fieldErrors, taskFieldErrors);

    if (hasAnyFieldError(fieldErrors)) {
        const message = isAgentic
            ? 'Please fill in all required fields (task prompt, golden response, and at least one rubric criterion)'
            : 'Please fill in all required fields (task prompt and answer)';
        errors.push(message);
    }

    return { isValid: errors.length === 0, errors, fieldErrors };
};

const generateRandomTaskId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const normalizeReferences = (references = []) => (
    references
        .filter(ref => ref?.filename)
        .map(ref => ({
            filename: ref.filename,
            mimeType: ref.mimeType || '',
            size: ref.size || 0,
            url: ref.url || ''
        }))
);

const getReferenceNames = (references = []) => (
    normalizeReferences(references).map(ref => ref.filename)
);

const getAgenticTaskReferenceField = (referenceNames = []) => {
    if (referenceNames.length > 1) return referenceNames;
    return referenceNames[0] || "";
};

export const convertEvalBuilderToFiles = (tasks, version, evalType = EVAL_TYPES.QA) => {
    const isAgentic = evalType === EVAL_TYPES.AGENTIC;
    const taskIdMap = new Map();
    for (const task of tasks) {
        let randomId;
        do {
            randomId = generateRandomTaskId();
        } while (taskIdMap.has(randomId));
        taskIdMap.set(task.id, randomId);
    }

    const tasksJson = tasks.map((task) => {
        const referenceNames = getReferenceNames(task.references || []);
        const baseTask = {
            task_id: taskIdMap.get(task.id),
            task_prompt: task.prompt,
            reference_file: isAgentic
                ? getAgenticTaskReferenceField(referenceNames)
                : (referenceNames[0] || "")
        };

        if (!isAgentic) {
            return baseTask;
        }

        return {
            ...baseTask,
            metadata: task.metadata || {},
            environment: task.environment || {}
        };
    });

    const answersJson = tasks.map((task) => {
        const referenceNames = getReferenceNames(task.references || []);
        const baseAnswer = {
            task_id: taskIdMap.get(task.id),
            answer: task.answer || '',
            reference_file: isAgentic ? referenceNames : (referenceNames[0] || ""),
            tools: task.tools || [],
            criteria: serializeCriteria(task.criteria || [], isAgentic),
            passThreshold: task.passThreshold || 70
        };

        return baseAnswer;
    });

    const tasksPayload = {
        version: version ?? 1,
        prompts: tasksJson
    };

    const tasksFile = new File(
        [JSON.stringify(tasksPayload, null, 2)],
        'task.json',
        { type: 'application/json' }
    );

    const answersFile = new File(
        [JSON.stringify(answersJson, null, 2)],
        'answers.json',
        { type: 'application/json' }
    );

    return { tasksFile, answersFile };
};

/**
 * Extracts unique reference files from a list of tasks.
 */
const getUniqueFiles = (tasks) => {
    const fileMap = new Map();
    for (const task of tasks) {
        
        task.references?.forEach(ref => {
            if (ref.file && !fileMap.has(ref.filename)) {
                fileMap.set(ref.filename, ref.file);
            }
        });
    }
    return fileMap;
};

/**
 * Creates a ZIP file from a Map of filenames and file objects.
 */
const createZipBlob = async (fileMap) => {
    const zip = new JSZip();
    
    const additions = Array.from(fileMap.entries()).map(async ([name, file]) => {
        const content = await file.arrayBuffer();
        zip.file(name, content);
    });

    await Promise.all(additions);
    return zip.generateAsync({ type: 'blob' });
};

export const processReferenceFiles = async (tasks) => {
    const referenceFilesMap = getUniqueFiles(tasks);

    if (referenceFilesMap.size === 0) {
        return null;
    }

    try {
        const zipBlob = await createZipBlob(referenceFilesMap);
        return new File([zipBlob], 'refs.zip', { type: 'application/zip' });
    } catch (error) {
        throw new Error('Error processing reference files');
    }
};

export const prepareEvalBuilderFiles = async (tasks, version, evalType = EVAL_TYPES.QA) => {
    const validation = validateEvalBuilderTasks(tasks, evalType);
    if (!validation.isValid) {
        return {
            files: null,
            errors: validation.errors,
            fieldErrors: validation.fieldErrors
        };
    }

    try {
        const { tasksFile, answersFile } = convertEvalBuilderToFiles(tasks, version, evalType);
        
        const referenceFile = await processReferenceFiles(tasks);
        
        const files = [tasksFile, answersFile];
        if (referenceFile) {
            files.push(referenceFile);
        }

        return {
            files,
            errors: [],
            fieldErrors: {}
        };
    } catch (error) {
        return {
            files: null,
            errors: [error.message || 'Error preparing files'],
            fieldErrors: {}
        };
    }
};
